"use client";

import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/lib/auth";
import { getActiveOrgSlug } from "@/lib/api";
import Turnstile from "@/components/ui/Turnstile";

/**
 * The Enterprise "Talk to sales" form — every Enterprise button opens this
 * (owner, 2026-10-04). It replaced mailto: links, which do nothing for anyone
 * on webmail, so leads were silently lost.
 *
 * Minimal friction: logged out, only a work email is required; logged in, there
 * is nothing to fill — the server attaches the account, workspace, plan and
 * usage. Posts to /api/leads/sales (stored as a lead, sent to the sales inbox).
 */

const API_BASE = process.env.NEXT_PUBLIC_API_URL || "/api";

const VOLUMES: { v: string; label: string }[] = [
  { v: "", label: "Envelopes a year (optional)" },
  { v: "under_1k", label: "Under 1,000" },
  { v: "1k_10k", label: "1,000 – 10,000" },
  { v: "10k_plus", label: "10,000+" },
  { v: "not_sure", label: "Not sure yet" },
];

function track(action: string, source: string) {
  try {
    (window as any).sfAnalytics?.track?.(`talk_to_sales_${action}`, "Sales", source);
  } catch {
    /* analytics must never break the form */
  }
}

export function TalkToSalesModal({ source, onClose }: { source: string; onClose: () => void }) {
  const { user, getAccessToken } = useAuth();
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [error, setError] = useState("");
  const [token, setToken] = useState<string | null>(null);
  const [resetKey, setResetKey] = useState(0);
  const loggedIn = !!user;
  // When no site key is configured (local dev), the widget renders nothing and
  // the server skips the check — don't block the button waiting for a token.
  const needsCaptcha = !loggedIn && !!process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

  // onClose is a fresh arrow on every parent render: hold it in a ref so the
  // effect (and the "opened" event) runs once per opening, not per re-render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    track("opened", source);
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onCloseRef.current(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [source]);

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    setStatus("sending");
    setError("");
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    const access = loggedIn ? getAccessToken() : null;
    if (access) headers.Authorization = `Bearer ${access}`;
    const slug = loggedIn ? getActiveOrgSlug() : null;
    if (slug) headers["X-Active-Org"] = slug;
    const body = {
      email: loggedIn ? undefined : String(form.get("email") || "").trim(),
      name: loggedIn ? undefined : String(form.get("name") || "").trim() || undefined,
      company: String(form.get("company") || "").trim() || undefined,
      volume: String(form.get("volume") || "") || undefined,
      message: String(form.get("message") || "").trim() || undefined,
      source,
      landing_path: window.location.pathname,
      turnstile_token: token,
      website: String(form.get("website") || ""), // honeypot
    };
    try {
      const res = await fetch(`${API_BASE}/leads/sales`, { method: "POST", headers, body: JSON.stringify(body) });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const detail = typeof data.detail === "string" ? data.detail
          : res.status === 401 ? "Your session has expired — please sign in again, or email hello@signforge.io."
          : "Please check your email address and try again.";
        throw new Error(detail);
      }
      setStatus("sent");
      track("sent", source);
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "Something went wrong. Please email hello@signforge.io.");
      setToken(null);
      setResetKey((k) => k + 1);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/40 backdrop-blur-[2px] p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="talk-to-sales-title"
      data-testid="talk-to-sales"
    >
      <div className="w-full max-w-md max-h-[90dvh] overflow-y-auto rounded-2xl bg-white p-6 sm:p-7 shadow-2xl shadow-black/20">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 id="talk-to-sales-title" className="font-display text-lg font-bold text-ink">
              Talk to sales
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Enterprise plans: SSO, API, volume pricing and a dedicated contact.
            </p>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-600 p-1 -m-1">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {status === "sent" ? (
          <div className="mt-6" data-testid="talk-to-sales-sent">
            <p className="text-sm font-semibold text-ink">Thanks &mdash; we&apos;ve got it.</p>
            <p className="mt-1 text-sm text-slate-600">
              We&apos;ll get back to you within one business day. A confirmation is on its way to your inbox.
            </p>
            <button onClick={onClose} className="mt-5 w-full rounded-lg bg-slate-100 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-200">
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-5 space-y-3">
            {/* Honeypot — hidden from people, filled by bots */}
            <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
              <label htmlFor="tts-website">Website</label>
              <input id="tts-website" name="website" type="text" tabIndex={-1} autoComplete="off" defaultValue="" />
            </div>

            {loggedIn ? (
              <p className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600">
                We&apos;ll reply to <span className="font-semibold text-slate-800">{user?.email}</span> and include
                your workspace and plan, so there&apos;s nothing else to fill in.
              </p>
            ) : (
              <>
                <input name="email" type="email" required placeholder="Work email" autoComplete="email"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100" />
                <div className="grid grid-cols-2 gap-3">
                  <input name="name" type="text" placeholder="Name (optional)" autoComplete="name" maxLength={255}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100" />
                  <input name="company" type="text" placeholder="Company (optional)" autoComplete="organization" maxLength={255}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100" />
                </div>
              </>
            )}
            <select name="volume" defaultValue=""
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm text-slate-700 bg-white focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100">
              {VOLUMES.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
            <textarea name="message" rows={3} maxLength={5000} placeholder="Anything we should know? (optional)"
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100" />

            {!loggedIn && (
              <Turnstile onVerify={setToken} onExpire={() => setToken(null)} onError={() => setToken(null)} resetKey={resetKey} />
            )}
            {status === "error" && <p className="text-sm text-red-600" role="alert">{error}</p>}

            <button type="submit" disabled={status === "sending" || (needsCaptcha && !token)}
              className="w-full rounded-lg bg-brand-600 py-2.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50 transition-colors">
              {status === "sending" ? "Sending…" : "Send"}
            </button>
            <p className="text-[11px] text-slate-400 text-center">
              Prefer email? <a href="mailto:hello@signforge.io" className="underline">hello@signforge.io</a>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

/** A "Talk to sales" / "Contact sales" button that opens the form. Pass
 *  ``autoOpenParam`` to also open it when the page URL carries ``?sales=1``
 *  (how the usage notice and banner deep-link to /contact). */
export default function TalkToSalesButton({
  source, className, children = "Contact sales", autoOpenParam = false, testId,
}: {
  source: string; className?: string; children?: React.ReactNode; autoOpenParam?: boolean; testId?: string;
}) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (autoOpenParam && new URLSearchParams(window.location.search).get("sales") === "1") setOpen(true);
  }, [autoOpenParam]);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={className} data-testid={testId}>
        {children}
      </button>
      {open && <TalkToSalesModal source={source} onClose={() => setOpen(false)} />}
    </>
  );
}
